// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import TitledSection from "@synd/App/Components/TitledSection/TitledSection";
import WidgetQuickLinks from "@synd/XDL/Icons/WidgetQuickLinks";
import * as React from "react";
import { FC, useMemo } from "react";

const enum fields {
    links = "links",
    text = "text",
}

export interface QuickLinkModel {
    fields: {
        linkUrl: LinkFieldValue;
        linkText: string;
        openInNewWindow: boolean;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: QuickLinkModel[];
    };
}

export interface QuickLinksStyles {
    container?: InjectableCss;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
}

export const quickLinksStyles: QuickLinksStyles = {};

type Props = OwnProps;

export const QuickLinks: FC<Props> = ({ fields }) => {
    const { links } = useGetLinks(fields.links, o => o.fields.linkUrl);
    const styles = quickLinksStyles;

    const mappedLinks = useMemo(
        () =>
            links
                ?.filter((link: any) => !link.excludeFromNavigation)
                ?.reduce((acc: QuickLinkModel[], link: any, index) => {
                    const { linkText, openInNewWindow } = fields.links?.[index]?.fields || {};
                    acc.push({ ...link, linkText, openInNewWindow });
                    return acc;
                }, []),
        [links],
    );
    if (!mappedLinks.length) {
        return null;
    }

    return (
        <TitledSection icon={WidgetQuickLinks} title={translate("Quick Links")}>
            <StyledWrapper {...styles.container}>
                {mappedLinks?.map((link: any) => {
                    return (
                        <StyledWrapper {...styles.linkWrapper} key={link.url}>
                            <Link href={link.url} target={link.openInNewWindow ? "_blank" : ""} {...styles.link}>
                                {link.linkText || link.title}
                            </Link>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        </TitledSection>
    );
};

const widgetModule: WidgetModule = {
    component: QuickLinks,
    definition: {
        group: "Basic",
        icon: "link",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        linkUrl: { type, value },
                        linkText,
                    } = item.fields;
                    if (linkText) {
                        return linkText;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: "linkUrl",
                        displayName: "link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        allowCategories: item => false,
                        isRequired: true,
                    },
                    {
                        name: "linkText",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
